import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import { initializeGA, setDefaultConsent, updateConsent } from '../utils/analytics';

/**
 * Determine the correct cookie domain for cross-subdomain sharing
 */
function getCookieDomain(): string {
  const hostname = window.location.hostname;
  // For localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return hostname;
  }
  // For production, use root domain to share across subdomains
  return '.brick.directory';
}

export function CookieConsentBanner() {
  useEffect(() => {
    // Set default consent before initializing
    setDefaultConsent();

    CookieConsent.run({
      // Disable page scripts to manually control GA initialization
      disablePageInteraction: false,

      // Cookie configuration
      cookie: {
        name: 'cc_cookie',
        domain: getCookieDomain(),
        path: '/',
        sameSite: 'Lax',
        expiresAfterDays: 365,
      },

      // GUI options
      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'bottom right',
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false,
        },
      },

      // Categories configuration
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: false,
          readOnly: false,
          autoClear: {
            cookies: [
              {
                name: /^_ga/, // Clear all GA cookies
              },
              {
                name: '_gid',
              },
            ],
          },
        },
      },

      // Translations
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description:
                'We use cookies to enhance your experience. Essential cookies are required for the site to function. Analytics cookies help us understand how you use our service and improve it. You can customize your preferences at any time.',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage preferences',
            },
            preferencesModal: {
              title: 'Cookie Preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'Cookie Usage',
                  description:
                    'We use cookies to provide essential functionality and to analyze how our service is used. You can choose which categories of cookies to accept.',
                },
                {
                  title: 'Strictly Necessary Cookies',
                  description:
                    'These cookies are essential for the website to function properly. They enable core functionality such as authentication and session management. These cookies cannot be disabled.',
                  linkedCategory: 'necessary',
                  cookieTable: {
                    headers: {
                      name: 'Cookie',
                      domain: 'Domain',
                      desc: 'Description',
                    },
                    body: [
                      {
                        name: 'cc_cookie',
                        domain: window.location.hostname,
                        desc: 'Stores your cookie preferences',
                      },
                    ],
                  },
                },
                {
                  title: 'Analytics Cookies',
                  description:
                    'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our service.',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: {
                      name: 'Cookie',
                      domain: 'Domain',
                      desc: 'Description',
                    },
                    body: [
                      {
                        name: '_ga',
                        domain: '.brick.directory',
                        desc: 'Google Analytics - Used to distinguish users',
                      },
                      {
                        name: '_gid',
                        domain: '.brick.directory',
                        desc: 'Google Analytics - Used to distinguish users',
                      },
                      {
                        name: '_ga_*',
                        domain: '.brick.directory',
                        desc: 'Google Analytics - Used to persist session state',
                      },
                    ],
                  },
                },
                {
                  title: 'More information',
                  description:
                    'For more details about cookies and your privacy, please read our <a href="/docs/privacy-policy.html">Privacy Policy</a> and <a href="/docs/cookie-policy.html">Cookie Policy</a>. If you have questions, contact us at <a href="mailto:privacy@brick.directory">privacy@brick.directory</a>.',
                },
              ],
            },
          },
        },
      },

      // Callbacks
      onChange: ({ cookie }) => {
        const analyticsEnabled = cookie.categories.includes('analytics');

        if (analyticsEnabled) {
          // User accepted analytics
          initializeGA();
          updateConsent(true);
        } else {
          // User rejected analytics
          updateConsent(false);
        }
      },

      onFirstConsent: ({ cookie }) => {
        const analyticsEnabled = cookie.categories.includes('analytics');

        if (analyticsEnabled) {
          initializeGA();
          updateConsent(true);
        }
      },

      onConsent: ({ cookie }) => {
        const analyticsEnabled = cookie.categories.includes('analytics');

        if (analyticsEnabled && !window.gtag) {
          initializeGA();
        }

        updateConsent(analyticsEnabled);
      },
    });
  }, []);

  return null; // This component doesn't render anything
}
